import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { BRAND_MINT } from '@/lib/utils'
import { useUnsavedChangesWarning } from '@/hooks/useUnsavedChangesWarning'
import {
  getAdminSettingsApi,
  updateAdminSettingsApi,
} from '@/lib/api'
import {
  LoaderIcon,
  SettingsIcon,
  SaveIcon,
  AlertTriangleIcon,
} from 'lucide-react'

// Known setting keys with friendly labels and descriptions
const SETTING_META: Record<string, { label: string; description: string; type: 'text' | 'number' | 'boolean' | 'json' }> = {
  platform_name: { label: 'Platform name', description: 'The display name shown across the platform.', type: 'text' },
  support_email: { label: 'Support email', description: 'Email address for user support inquiries.', type: 'text' },
  maintenance_mode: { label: 'Maintenance mode', description: 'When enabled, only super_admins can access the platform.', type: 'boolean' },
  max_users_per_org: { label: 'Max users per org', description: 'Maximum number of users allowed per organization.', type: 'number' },
  max_pots_per_org: { label: 'Max circles per org', description: 'Maximum number of active funding circles per organization.', type: 'number' },
  default_currency: { label: 'Default currency', description: 'Default currency code for new organizations.', type: 'text' },
  session_timeout_minutes: { label: 'Session timeout (min)', description: 'Maximum session duration in minutes before forced re-auth.', type: 'number' },
  max_login_attempts: { label: 'Max login attempts', description: 'Number of failed login attempts before temporary lockout.', type: 'number' },
}

type SettingValue = string | number | boolean | object

export const Route = createFileRoute('/dashboard/admin/settings')({
  component: () => {
    const [settings, setSettings] = useState<Record<string, SettingValue>>({})
    const [meta, setMeta] = useState<Array<{ key: string; description: string; updated_at: string }>>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [dirty, setDirty] = useState(false)
    useUnsavedChangesWarning(dirty)

    useEffect(() => {
      let cancelled = false

      async function load() {
        try {
          const res = await getAdminSettingsApi()
          if (!cancelled) {
            setSettings(res.settings)
            setMeta(res._meta)
          }
        } catch (err) {
          if (!cancelled) {
            toast.error(
              err instanceof Error ? err.message : 'Failed to load settings',
            )
          }
        } finally {
          if (!cancelled) setLoading(false)
        }
      }

      load()
      return () => { cancelled = true }
    }, [])

    const handleChange = (key: string, value: SettingValue) => {
      setSettings((prev) => ({ ...prev, [key]: value }))
      setDirty(true)
    }

    const handleSave = async () => {
      setSaving(true)
      try {
        const res = await updateAdminSettingsApi(settings)
        setDirty(false)
        toast.success(res.message)
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Failed to save settings',
        )
      } finally {
        setSaving(false)
      }
    }

    const renderSettingInput = (key: string, value: SettingValue) => {
      const metaInfo = SETTING_META[key] || { label: key, description: '', type: 'text' }

      switch (metaInfo.type) {
        case 'boolean':
          return (
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={value === true || value === 'true'}
                onChange={(e) => handleChange(key, e.target.checked)}
                className="size-4 rounded border-border accent-primary"
              />
              <span className="text-sm font-medium text-foreground">
                {value === true || value === 'true' ? 'Enabled' : 'Disabled'}
              </span>
            </label>
          )
        case 'number':
          return (
            <input
              type="number"
              value={String(value ?? '')}
              onChange={(e) => handleChange(key, e.target.value ? Number(e.target.value) : '')}
              className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary tabular-nums"
            />
          )
        case 'json':
          return (
            <textarea
              value={typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value ?? '')}
              onChange={(e) => {
                try {
                  handleChange(key, JSON.parse(e.target.value))
                } catch {
                  handleChange(key, e.target.value)
                }
              }}
              rows={3}
              className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm font-mono outline-none focus:border-primary"
            />
          )
        default:
          return (
            <input
              type="text"
              value={String(value ?? '')}
              onChange={(e) => handleChange(key, e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            />
          )
      }
    }

    if (loading) {
      return (
        <div className="mx-auto max-w-2xl">
          <div className="mb-8">
            <div className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="mt-0.5 shrink-0"
                style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
              />
              <h1 className="text-2xl font-semibold">Admin settings</h1>
            </div>
          </div>
          <div className="flex items-center justify-center rounded-xl border border-border bg-card py-16">
            <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
            <p className="ml-2 text-sm text-muted-foreground">Loading settings…</p>
          </div>
        </div>
      )
    }

    return (
      <div className="mx-auto max-w-2xl">
        <div className="mb-8">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 shrink-0"
              style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
            />
            <div className="flex flex-1 items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-semibold">Admin settings</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  System-wide configuration and preferences.
                </p>
              </div>
              <Button
                onClick={handleSave}
                disabled={saving || !dirty}
                className="gap-1.5"
              >
                {saving ? (
                  <LoaderIcon className="size-4 animate-spin" />
                ) : (
                  <SaveIcon className="size-4" />
                )}
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </div>
        </div>

        {Object.keys(settings).length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-16">
            <SettingsIcon className="size-10 text-muted-foreground/40" />
            <p className="text-sm font-medium text-foreground">No settings configured</p>
            <p className="text-sm text-muted-foreground">
              Platform settings will appear here once they are configured.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(settings).map(([key, value]) => {
              const metaInfo = SETTING_META[key] || {
                label: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
                description: meta.find((m) => m.key === key)?.description || '',
                type: 'text',
              }

              return (
                <div
                  key={key}
                  className="rounded-xl border border-border bg-card p-5"
                >
                  <div className="mb-3">
                    <label className="text-sm font-semibold text-foreground">
                      {metaInfo.label}
                    </label>
                    {metaInfo.description && (
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {metaInfo.description}
                      </p>
                    )}
                  </div>
                  {renderSettingInput(key, value)}
                  {meta.find((m) => m.key === key) && (
                    <p className="mt-2 text-[10px] text-muted-foreground/60">
                      Last updated:{' '}
                      {new Date(meta.find((m) => m.key === key)!.updated_at).toLocaleString()}
                    </p>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {dirty && (
          <div className="mt-6 flex items-center gap-2 rounded-lg bg-amber-500/10 px-4 py-3 text-sm text-amber-600">
            <AlertTriangleIcon className="size-4 shrink-0" />
            <span>You have unsaved changes. Click "Save changes" to apply them.</span>
          </div>
        )}
      </div>
    )
  },
})
